import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaDownload,
  FaTrash,
  FaSpinner,
  FaCloudUploadAlt,
  FaTimes,
} from "react-icons/fa";
import "./Firmware.scss";
import { useTranslation } from "react-i18next";

import {
  browseFirmware,
  getFirmwareFolders,
  createFirmwareFolder,
  renameFirmwareFolder,
  moveFirmwareFolder,
  deleteFirmwareFolder,
  deleteFirmwareFile,
  deleteFirmwareDevice,
} from "../services/firmwareService";

import { useAuth } from "../services/authService";
import {
  canManageFirmware,
  normalizeStatus,
  normalizeRole,
} from "../services/authz";

import {
  signFirmwareDownload,
  signFirmwareUpload,
  createFirmwareMetadata,
} from "../services/apiBase";

// Ensure every navigable prefix ends with "/" so URL, state and backend stay consistent.
function ensureTrailingSlash(p) {
  if (!p) return "";
  return p.endsWith("/") ? p : `${p}/`;
}

// Split a firmware path/prefix into path segments.
function splitPrefix(prefix) {
  const clean = String(prefix || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/\/+/g, "/");

  return clean.split("/").filter(Boolean);
}

// Create a canonical path segment without diacritics for safe R2 / Firestore paths.
function slugifySegment(input) {
  const s = String(input || "").trim();
  if (!s) return "";

  const noDia = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const cleaned = noDia
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^[-_.]+|[-_.]+$/g, "");

  return cleaned;
}

// Fallback file label used only when originalFilename is not available in metadata.
function prettyFileName(file) {
  return file?.name || file?.path || file?.key || file?.id || "—";
}

// Resolve the storage key for a file row.
function getFileKey(file) {
  return file?.key || file?.path || null;
}

// Guess content type for uploaded file.
function guessContentType(file) {
  return file?.type && String(file.type).trim()
    ? file.type
    : "application/octet-stream";
}

// Convert byte size into a human-readable string.
function humanSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";

  const units = ["B", "kB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;

  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }

  const digits = i === 0 ? 0 : v < 10 ? 1 : 0;
  return `${v.toFixed(digits)} ${units[i]}`;
}

// Shorten long note text for table display while keeping full value in title tooltip.
function shortText(s, max = 50) {
  const t = String(s || "").trim();
  if (!t) return "";
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

// Format upload date into one stable UI format for all languages.
// This avoids locale-specific separators such as "23. 03. 2026" vs "23/03/2026".
function formatCreatedAt(value) {
  if (!value) return "—";

  try {
    let date;

    if (typeof value?.toDate === "function") {
      date = value.toDate();
    } else if (
      typeof value === "object" &&
      value !== null &&
      typeof value._seconds === "number"
    ) {
      date = new Date(value._seconds * 1000);
    } else if (typeof value === "string" || typeof value === "number") {
      date = new Date(value);
    } else {
      return "—";
    }

    if (Number.isNaN(date.getTime())) return "—";

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = String(date.getFullYear());

    return `${dd}.${mm}.${yyyy}`;
  } catch {
    return "—";
  }
}

// Normalize folder item coming either as plain string or as backend object.
function normalizeFolderItem(prefix, f) {
  if (typeof f === "string") {
    return {
      name: f,
      displayName: f,
      path: ensureTrailingSlash(`${prefix}${f}`),
      status: "active",
      fileCount: undefined,
    };
  }

  const name = String(f?.name || "").trim();
  const displayName = String(f?.displayName || f?.name || "").trim();
  const path = ensureTrailingSlash(String(f?.path || `${prefix}${name}`));

  return {
    name,
    displayName: displayName || name,
    path,
    status: f?.status || "active",
    fileCount: typeof f?.fileCount === "number" ? f.fileCount : undefined,
  };
}

// Upload a file to a signed URL and report progress through XMLHttpRequest.
function putFileWithProgress({ uploadUrl, file, contentType, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", uploadUrl, true);
    xhr.setRequestHeader("Content-Type", contentType);

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      const pct = Math.round((evt.loaded / evt.total) * 100);
      onProgress?.(pct);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };

    xhr.onerror = () => reject(new Error("Upload failed (network error)"));
    xhr.send(file);
  });
}

// Merge file rows by key/path and keep richer metadata when duplicates appear.
function mergeFilesByKey(primaryFiles = [], secondaryFiles = []) {
  const map = new Map();

  [...primaryFiles, ...secondaryFiles].forEach((file) => {
    const key = file?.key || file?.path || file?.name;
    if (!key) return;

    const prev = map.get(key);

    if (!prev) {
      map.set(key, file);
      return;
    }

    const merged = {
      ...prev,
      ...file,
      originalFilename:
        file?.originalFilename || prev?.originalFilename || null,
      notes: file?.notes || prev?.notes || null,
      version: file?.version || prev?.version || null,
      createdAt: file?.createdAt || prev?.createdAt || null,
      createdByEmail: file?.createdByEmail || prev?.createdByEmail || null,
      size:
        typeof file?.size === "number"
          ? file.size
          : typeof prev?.size === "number"
            ? prev.size
            : null,
    };

    map.set(key, merged);
  });

  return Array.from(map.values()).sort((a, b) =>
    String(a?.name || a?.originalFilename || "")
      .toLowerCase()
      .localeCompare(
        String(b?.name || b?.originalFilename || "").toLowerCase(),
        "cs",
      ),
  );
}

// Fixed third-level firmware channels.
// These are kept sorted in a predefined order in the UI.
const ALLOWED_CHANNELS = ["release", "beta", "archive"];
const CHANNEL_ORDER = { archive: 0, beta: 1, release: 2 };

export default function Firmware() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Resolve current user's normalized permission state.
  const { userRole, userStatus } = useAuth();
  const status = normalizeStatus(userStatus);
  const role = normalizeRole(userRole);
  const canManage = canManageFirmware(status, role);

  // Core browser state for the firmware section.
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [prefix, setPrefix] = useState("");
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Secondary UI status for warnings and current download.
  const [warning, setWarning] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);

  // Breadcrumb labels returned by backend allow pretty folder names in the UI.
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  // Upload form state.
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Upload modal / drag&drop state.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // When browsing variant root, we may optionally load files from hidden "release/".
  const [variantReleaseFiles, setVariantReleaseFiles] = useState([]);
  const [variantReleaseLoading, setVariantReleaseLoading] = useState(false);

  // Delete state for folders and files.
  const [deletingFolderPath, setDeletingFolderPath] = useState(null);
  const [deletingFileKey, setDeletingFileKey] = useState(null);

  // Delete state for whole device root.
  const [deletingDevice, setDeletingDevice] = useState(false);

  // Rename modal state for folders.
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState("");

  // These refs prevent circular URL <-> state update loops.
  const lastPushedPrefixRef = useRef("");
  const suppressDevicePushRef = useRef(false);

  // Read firmware prefix from query string.
  function getPrefixFromUrl(search) {
    const sp = new URLSearchParams(search || "");
    const raw = sp.get("prefix");
    return raw ? ensureTrailingSlash(raw) : "";
  }

  // Push current firmware prefix into URL so the view is deep-linkable.
  function pushPrefixToUrl(nextPrefix) {
    const p = ensureTrailingSlash(nextPrefix || "");
    if (!p) return;
    if (p === lastPushedPrefixRef.current) return;

    lastPushedPrefixRef.current = p;

    const sp = new URLSearchParams(location.search || "");
    sp.set("prefix", p);

    navigate(
      { pathname: location.pathname, search: `?${sp.toString()}` },
      { replace: false },
    );
  }

  // Refresh top-level devices/systems list from backend.
  async function refreshDevices(selectAfter = null) {
    const data = await getFirmwareFolders();
    const list = Array.isArray(data?.folders) ? data.folders : [];
    setDevices(list);

    if (selectAfter && list.includes(selectAfter)) {
      setSelectedDevice(selectAfter);
      const next = ensureTrailingSlash(selectAfter);
      setPrefix(next);
      pushPrefixToUrl(next);
    }
  }

  // Initial device list load.
  useEffect(() => {
    setError(null);
    refreshDevices().catch((err) => setError(err?.message || String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh current folder contents from backend.
  async function refreshBrowse(currentPrefix) {
    setLoading(true);
    setError(null);

    try {
      const data = await browseFirmware(currentPrefix);
      setFolders(Array.isArray(data?.folders) ? data.folders : []);
      setFiles(Array.isArray(data?.files) ? data.files : []);
      setBreadcrumbItems(
        Array.isArray(data?.breadcrumb) ? data.breadcrumb : [],
      );
    } catch (err) {
      setError(err?.message || String(err));
      setFolders([]);
      setFiles([]);
      setBreadcrumbItems([]);
    } finally {
      setLoading(false);
    }
  }

  // Load files from hidden release/ folder when browsing variant root.
  // This is only a fallback for backends that do not return current files directly on variant root.
  async function refreshVariantReleaseFiles(currentPrefix) {
    const parts = splitPrefix(currentPrefix);
    const isVariantRoot = parts.length === 2;

    if (!isVariantRoot) {
      setVariantReleaseFiles([]);
      return;
    }

    const releasePrefix = ensureTrailingSlash(`${currentPrefix}release/`);
    setVariantReleaseLoading(true);

    try {
      const data = await browseFirmware(releasePrefix);
      setVariantReleaseFiles(Array.isArray(data?.files) ? data.files : []);
    } catch {
      setVariantReleaseFiles([]);
    } finally {
      setVariantReleaseLoading(false);
    }
  }

  async function refreshCurrentView(currentPrefix) {
    await refreshBrowse(currentPrefix);
    await refreshVariantReleaseFiles(currentPrefix);
  }

  // Sync URL -> local state.
  useEffect(() => {
    const urlPrefix = getPrefixFromUrl(location.search);

    if (!urlPrefix) {
      setPrefix("");
      setSelectedDevice("");
      setFolders([]);
      setFiles([]);
      setBreadcrumbItems([]);
      setVariantReleaseFiles([]);
      return;
    }

    setPrefix(urlPrefix);

    const parts = splitPrefix(urlPrefix);
    const dev = parts[0] || "";

    if (dev && dev !== selectedDevice) {
      suppressDevicePushRef.current = true;
      setSelectedDevice(dev);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Sync selected device -> device root path.
  useEffect(() => {
    if (!selectedDevice) return;

    if (suppressDevicePushRef.current) {
      suppressDevicePushRef.current = false;
      return;
    }

    const next = ensureTrailingSlash(selectedDevice);
    setPrefix(next);
    pushPrefixToUrl(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice]);

  // Browse whenever the current prefix changes.
  useEffect(() => {
    if (!prefix) {
      setVariantReleaseFiles([]);
      return;
    }

    refreshCurrentView(prefix);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);

  // Raw breadcrumb path parts.
  const breadcrumb = useMemo(() => splitPrefix(prefix), [prefix]);

  // Prefer backend display labels, but build a fallback breadcrumb if needed.
  const breadcrumbDisplay = useMemo(() => {
    if (Array.isArray(breadcrumbItems) && breadcrumbItems.length) {
      return breadcrumbItems;
    }

    return breadcrumb.map((p, idx) => ({
      path: `${breadcrumb.slice(0, idx + 1).join("/")}/`,
      name: p,
      displayName: p,
    }));
  }, [breadcrumbItems, breadcrumb]);

  // Upload is allowed:
  // - on variant root: device / variant  -> upload goes into hidden release/
  // - inside any third-level folder: device / variant / folder
  const uploadContext = useMemo(() => {
    const parts = breadcrumb;
    const device = parts[0] || "";
    const variant = parts[1] || "";
    const folder = parts[2] || "";
    const depth = parts.length;

    const isVariantRoot = depth === 2 && Boolean(device) && Boolean(variant);
    const isFolderLevel =
      depth === 3 && Boolean(device) && Boolean(variant) && Boolean(folder);

    const ready = isVariantRoot || isFolderLevel;
    const storageFolder = isVariantRoot ? "release" : folder;

    return {
      device,
      variant,
      folder,
      depth,
      isVariantRoot,
      isFolderLevel,
      isChannel: ALLOWED_CHANNELS.includes(folder),
      ready,
      storageFolder,
    };
  }, [breadcrumb]);

  const showUpload = canManage && uploadContext.ready;
  const isAnyLoading = loading || variantReleaseLoading;

  // Update local prefix and URL together.
  function setPrefixAndUrl(nextPrefix) {
    const p = ensureTrailingSlash(nextPrefix);
    setPrefix(p);
    pushPrefixToUrl(p);
  }

  // Enter a folder directly by its path.
  function enterFolderByPath(folderPath) {
    const p = ensureTrailingSlash(folderPath);
    if (!p) return;
    setPrefixAndUrl(p);
  }

  // Navigate to a breadcrumb level.
  function goToCrumb(index) {
    const item = breadcrumbDisplay[index];
    if (!item?.path) return;
    setPrefixAndUrl(item.path);
  }

  function resetUploadForm() {
    setDragActive(false);
    setUploadFile(null);
    setUploadNotes("");
    setUploadProgress(0);
  }

  function openUploadModal() {
    if (!showUpload) return;
    setError(null);
    setWarning(null);
    setUploadOpen(true);
  }

  function closeUploadModal() {
    if (uploading) return;
    setUploadOpen(false);
    resetUploadForm();
  }

  function handlePickedFile(file) {
    if (!file) return;
    setUploadFile(file);
    setError(null);
  }

  function onDropZoneDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    setDragActive(true);
  }

  function onDropZoneDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }

  function onDropZoneDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;

    setDragActive(false);

    const file = e.dataTransfer?.files?.[0] || null;
    if (file) handlePickedFile(file);
  }

  // Create a new top-level system/device.
  async function handleCreateDevice() {
    if (!canManage) return;

    try {
      setError(null);

      const raw = window.prompt(t("firmwarePage.promptSystemName"));
      if (!raw || !raw.trim()) return;

      const name = raw.trim().replace(/\\/g, "/").replace(/\//g, "");
      if (!name) throw new Error(t("firmwarePage.errors.invalidSystemName"));

      if (devices.includes(name)) {
        setSelectedDevice(name);
        return;
      }

      const res = await createFirmwareFolder({
        prefix: ensureTrailingSlash(name),
        displayName: name,
      });

      const canonicalPath = ensureTrailingSlash(res?.path || name);
      await refreshDevices(splitPrefix(canonicalPath)[0] || name);
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  // Delete whole selected system/device.
  async function handleDeleteDevice() {
    if (!canManage) return;
    if (!selectedDevice) return;

    const dev = selectedDevice;
    const ok = window.confirm(t("firmwarePage.confirmDeleteSystem", { dev }));
    if (!ok) return;

    try {
      setError(null);
      setDeletingDevice(true);

      await deleteFirmwareDevice({ device: dev });

      setSelectedDevice("");
      setPrefix("");
      pushPrefixToUrl("");

      await refreshDevices();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setDeletingDevice(false);
    }
  }

  // Download file using signed backend URL.
  // Text files attempt blob download first so browser keeps the original filename.
  async function handleDownload(file) {
    const key = getFileKey(file);

    if (!key) {
      setError(t("firmwarePage.errors.fileKeyMissing"));
      return;
    }

    try {
      setError(null);
      setDownloadingKey(key);

      const data = await signFirmwareDownload(key);
      const url = data?.downloadUrl;
      if (!url) throw new Error(t("firmwarePage.errors.downloadUrlMissing"));

      const downloadName = file?.originalFilename || file?.name || "download";

      const isText = /\.(txt|log|json|xml|csv|md)$/i.test(downloadName || "");

      if (isText) {
        try {
          const resp = await fetch(url);

          if (resp.ok) {
            const blob = await resp.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", downloadName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            return;
          }
        } catch (err) {
          console.warn(
            "Blob download failed, falling back to direct link.",
            err,
          );
        }
      }

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setDownloadingKey(null);
    }
  }

  // Delete a single file from the current folder.
  async function handleDeleteFile(file) {
    if (!canManage) return;

    const key = getFileKey(file);
    if (!key) return;

    const ok = window.confirm(
      t("firmwarePage.confirmDeleteFile", {
        name: file?.originalFilename || prettyFileName(file),
      }),
    );
    if (!ok) return;

    try {
      setError(null);
      setDeletingFileKey(key);
      await deleteFirmwareFile({ key });
      await refreshCurrentView(prefix);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setDeletingFileKey(null);
    }
  }

  // Delete a folder from the current level.
  async function handleDeleteFolder(folderItemRaw) {
    if (!canManage) return;

    const f = normalizeFolderItem(prefix, folderItemRaw);
    const ok = window.confirm(
      t("firmwarePage.confirmDeleteFolder", { name: f.displayName }),
    );
    if (!ok) return;

    try {
      setError(null);
      setDeletingFolderPath(f.path);
      await deleteFirmwareFolder({ path: f.path });

      if (prefix === f.path) {
        const parent = splitPrefix(prefix).slice(0, -1).join("/");
        setPrefixAndUrl(parent ? `${parent}/` : "");
      } else {
        await refreshCurrentView(prefix);
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setDeletingFolderPath(null);
    }
  }

  // Upload file flow:
  // 1) request signed upload URL
  // 2) PUT file to R2
  // 3) save metadata in Firestore
  async function handleUpload() {
    if (!canManage) return;
    if (uploading) return;

    try {
      setError(null);
      setWarning(null);

      if (!showUpload) {
        throw new Error(t("firmwarePage.errors.uploadOnlyInTargetFolder"));
      }

      if (!uploadFile) {
        throw new Error(t("firmwarePage.errors.uploadFileMissing"));
      }

      setUploading(true);
      setUploadProgress(0);

      const ct = guessContentType(uploadFile);

      const signRes = await signFirmwareUpload({
        device: uploadContext.device,
        variant: uploadContext.variant,
        channel: uploadContext.storageFolder,
        filename: uploadFile.name,
        contentType: ct,
        size: uploadFile.size,
      });

      const uploadUrl = signRes?.uploadUrl;
      const key = signRes?.key;

      if (!uploadUrl || !key) {
        throw new Error(t("firmwarePage.errors.uploadUrlMissing"));
      }

      await putFileWithProgress({
        uploadUrl,
        file: uploadFile,
        contentType: ct,
        onProgress: (pct) => setUploadProgress(pct),
      });

      try {
        await createFirmwareMetadata({
          key,
          device: uploadContext.device,
          variant: uploadContext.variant,
          folder: uploadContext.storageFolder,
          channel: uploadContext.storageFolder,
          originalFilename: uploadFile.name,
          contentType: ct,
          size: uploadFile.size,
          notes: uploadNotes.trim() ? uploadNotes.trim() : null,
        });
      } catch (metaErr) {
        console.error("createFirmwareMetadata failed:", metaErr);
        setWarning(t("firmwarePage.warnings.metadataSaveFailed"));
      }

      setUploadProgress(100);
      await refreshCurrentView(prefix);
      setUploadOpen(false);
      resetUploadForm();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setUploading(false);
    }
  }

  // Create either:
  // - a new variant under a system
  // - a new custom folder under a variant
  async function handleCreateFolder() {
    if (!canManage) return;

    try {
      setError(null);

      if (!selectedDevice) {
        throw new Error(t("firmwarePage.errors.selectSystem"));
      }

      const currentParts = splitPrefix(prefix);
      const depth = currentParts.length;

      if (depth !== 1 && depth !== 2) {
        throw new Error(t("firmwarePage.errors.newFolderOnlyAtAllowedLevel"));
      }

      const name = window.prompt(
        depth === 1
          ? t("firmwarePage.promptVariantName")
          : t("firmwarePage.promptFolderName"),
      );

      if (!name || !name.trim()) return;

      const cleanDisplay = name.trim().replace(/\\/g, "/").replace(/\//g, "");
      if (!cleanDisplay) {
        throw new Error(t("firmwarePage.errors.invalidFolderDisplayName"));
      }

      const slug = slugifySegment(cleanDisplay);
      if (!slug) {
        throw new Error(t("firmwarePage.errors.invalidFolderName"));
      }

      if (depth === 1) {
        const variantRes = await createFirmwareFolder({
          prefix: ensureTrailingSlash(`${selectedDevice}/${slug}`),
          displayName: cleanDisplay,
        });

        const variantPath = ensureTrailingSlash(variantRes?.path);

        for (const ch of ALLOWED_CHANNELS) {
          await createFirmwareFolder({
            prefix: `${variantPath}${ch}/`,
            displayName: ch,
          });
        }

        await refreshCurrentView(`${selectedDevice}/`);
      } else {
        await createFirmwareFolder({
          prefix: ensureTrailingSlash(`${prefix}${slug}`),
          displayName: cleanDisplay,
        });

        await refreshCurrentView(prefix);
      }
    } catch (e) {
      setError(e?.message || String(e));
    }
  }

  // Open rename modal for editable folders.
  function openRenameForFolderItem(folderItemRaw) {
    const folder = normalizeFolderItem(prefix, folderItemRaw);

    if (ALLOWED_CHANNELS.includes(folder.name)) {
      setError(t("firmwarePage.errors.channelRenameNotAllowed"));
      return;
    }

    setRenameError("");
    setRenameSaving(false);
    setRenameTarget({ path: folder.path, currentName: folder.name });
    setRenameValue(folder.displayName || folder.name);
    setRenameOpen(true);
  }

  // Close rename modal and clear temporary state.
  function closeRename() {
    setRenameOpen(false);
    setRenameTarget(null);
    setRenameValue("");
    setRenameSaving(false);
    setRenameError("");
  }

  // Rename folder by moving its underlying path and then updating display metadata.
  async function handleRenameSubmit(e) {
    e?.preventDefault?.();
    if (!canManage) return;
    if (!renameTarget?.path) return;

    const newNameRaw = String(renameValue || "").trim();

    if (!newNameRaw) {
      setRenameError(t("firmwarePage.errors.renameEmpty"));
      return;
    }

    const displayName = newNameRaw.replace(/\\/g, "/").replace(/\//g, "");

    if (!displayName) {
      setRenameError(t("firmwarePage.errors.renameInvalid"));
      return;
    }

    const slug = slugifySegment(displayName);

    if (!slug) {
      setRenameError(t("firmwarePage.errors.renameInvalidNormalized"));
      return;
    }

    setRenameSaving(true);
    setRenameError("");

    try {
      const oldPath = ensureTrailingSlash(renameTarget.path);
      const parts = splitPrefix(oldPath);
      const parentParts = parts.slice(0, -1);
      const parentPath = parentParts.length ? `${parentParts.join("/")}/` : "";
      const requestedNewPath = ensureTrailingSlash(`${parentPath}${slug}`);

      if (requestedNewPath === oldPath) {
        setRenameSaving(false);
        closeRename();
        return;
      }

      const moveRes = await moveFirmwareFolder({
        fromPrefix: oldPath,
        toPrefix: requestedNewPath,
      });

      const canonicalNewPath = ensureTrailingSlash(
        moveRes?.toPrefix || requestedNewPath,
      );

      try {
        await renameFirmwareFolder({
          path: canonicalNewPath,
          newDisplayName: displayName,
        });
      } catch {
        // Ignore display-name metadata update failure after a successful move.
      }

      if (prefix.startsWith(oldPath)) {
        const nextPrefix = canonicalNewPath + prefix.slice(oldPath.length);
        setPrefixAndUrl(nextPrefix);
      } else {
        await refreshCurrentView(prefix);
      }

      closeRename();
    } catch (err) {
      const msg = err?.message || "";

      if (msg.includes("TARGET_PREFIX_ALREADY_EXISTS")) {
        setRenameError(t("firmwarePage.errors.targetPrefixExists"));
      } else if (msg.includes("MOVE_MUST_STAY_IN_SAME_PARENT")) {
        setRenameError(t("firmwarePage.errors.moveSameParentOnly"));
      } else if (msg.includes("ROOT_FOLDER_MOVE_NOT_ALLOWED")) {
        setRenameError(t("firmwarePage.errors.rootMoveNotAllowed"));
      } else {
        setRenameError(t("firmwarePage.errors.renameFailed"));
      }

      setRenameSaving(false);
    }
  }

  // Normalize and sort folder items, with fixed channels ordered first.
  const folderItems = useMemo(() => {
    const list = (folders || [])
      .map((f) => normalizeFolderItem(prefix, f))
      .filter((f) => f.name && f.path);

    list.sort((a, b) => {
      const aIsCh = ALLOWED_CHANNELS.includes(a.name);
      const bIsCh = ALLOWED_CHANNELS.includes(b.name);

      if (aIsCh && bIsCh) {
        return (CHANNEL_ORDER[a.name] ?? 99) - (CHANNEL_ORDER[b.name] ?? 99);
      }

      if (aIsCh && !bIsCh) return -1;
      if (!aIsCh && bIsCh) return 1;

      const aLabel = String(a.displayName || a.name || "").toLowerCase();
      const bLabel = String(b.displayName || b.name || "").toLowerCase();
      return aLabel.localeCompare(bLabel, "cs");
    });

    return list;
  }, [folders, prefix]);

  const visibleFiles = useMemo(() => {
    if (!uploadContext.isVariantRoot) {
      return files;
    }

    return mergeFilesByKey(files, variantReleaseFiles);
  }, [uploadContext.isVariantRoot, files, variantReleaseFiles]);

  const hasVariantVisibleFiles =
    uploadContext.isVariantRoot && visibleFiles.length > 0;

  const visibleFolderItems = useMemo(() => {
    if (!uploadContext.isVariantRoot) {
      return folderItems;
    }

    // Hide release only when variant root already shows current firmware.
    if (hasVariantVisibleFiles) {
      return folderItems.filter((f) => f.name !== "release");
    }

    return folderItems;
  }, [folderItems, uploadContext.isVariantRoot, hasVariantVisibleFiles]);

  return (
    <div className="page firmware-page">
      <div className="firmware__container">
        <h1 className="firmware__title">
          {t(["firmwarePage.title", "fw_title"])}
        </h1>

        {error && <p className="firmware__error">{error}</p>}
        {warning && <p className="firmware__warnText">{warning}</p>}

        <div className="firmware__deviceRow">
          <label className="firmware__label">
            {t("firmwarePage.selectSystem")}
            <select
              className="firmware__select"
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              disabled={deletingDevice}
            >
              <option value="">
                {t(["firmwarePage.selectPlaceholder", "select_placeholder"])}
              </option>
              {devices.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>

          {canManage && (
            <>
              <button
                className="firmware__btn firmware__btn--primary"
                onClick={handleCreateFolder}
                disabled={isAnyLoading || !selectedDevice || deletingDevice}
                title={
                  !selectedDevice
                    ? t("firmwarePage.errors.selectSystem")
                    : t(["firmwarePage.newFolder", "new_folder"])
                }
              >
                {t(["firmwarePage.newFolder", "new_folder"])}
              </button>

              <button
                className="firmware__btn firmware__btn--primary"
                onClick={handleCreateDevice}
                disabled={isAnyLoading || deletingDevice}
                title={t(["firmwarePage.addSystem", "add_system"])}
              >
                {t(["firmwarePage.addSystem", "add_system"])}
              </button>

              <button
                className="firmware__btn firmware__btn--danger"
                onClick={handleDeleteDevice}
                disabled={isAnyLoading || !selectedDevice || deletingDevice}
                title={
                  !selectedDevice
                    ? t("firmwarePage.errors.selectSystem")
                    : t(["firmwarePage.deleteSystem", "delete_system"])
                }
              >
                {deletingDevice
                  ? t(["firmwarePage.deleting", "deleting"])
                  : t(["firmwarePage.deleteSystem", "delete_system"])}
              </button>
            </>
          )}
        </div>

        {selectedDevice && (
          <div className="firmware__content">
            <div className="firmware__path">
              <b>{t(["firmwarePage.path", "path"])}</b>{" "}
              <span className="firmware__crumbs">
                {breadcrumbDisplay.map((item, idx) => (
                  <span
                    key={`${item.path || item.displayName}-${idx}`}
                    className="firmware__crumb"
                  >
                    <button
                      type="button"
                      className="firmware__crumbBtn"
                      onClick={() => goToCrumb(idx)}
                      disabled={
                        idx === breadcrumbDisplay.length - 1 || isAnyLoading
                      }
                      title={
                        idx === breadcrumbDisplay.length - 1
                          ? t("firmwarePage.currentFolder")
                          : t("firmwarePage.goToLevel")
                      }
                    >
                      {item.displayName || item.name}
                    </button>
                    {idx < breadcrumbDisplay.length - 1 && (
                      <span className="firmware__crumbSep">/</span>
                    )}
                  </span>
                ))}
              </span>
            </div>

            {showUpload && (
              <div className="firmware__toolbar">
                <button
                  type="button"
                  className="firmware__btn firmware__btn--success"
                  onClick={openUploadModal}
                  disabled={uploading}
                >
                  <FaCloudUploadAlt />
                  <span>{t("firmwarePage.openUploadModal")}</span>
                </button>
              </div>
            )}

            {isAnyLoading && (
              <p className="firmware__loading">{t("firmwarePage.loading")}</p>
            )}

            {!isAnyLoading && (
              <>
                {visibleFolderItems.length > 0 && (
                  <div className="firmware__section">
                    <h3 className="firmware__sectionTitle">
                      {t(["firmwarePage.folders", "folders"])}
                    </h3>

                    <ul className="firmware__folderList">
                      {visibleFolderItems.map((f) => {
                        const isDeleting = deletingFolderPath === f.path;
                        const isChannel = ALLOWED_CHANNELS.includes(f.name);

                        return (
                          <li key={f.path} className="firmware__folderItem">
                            <button
                              className="firmware__linkBtn"
                              onClick={() => enterFolderByPath(f.path)}
                              disabled={isAnyLoading}
                              title={
                                typeof f.fileCount === "number"
                                  ? t("firmwarePage.filesCount", {
                                      count: f.fileCount,
                                    })
                                  : t([
                                      "firmwarePage.openFolder",
                                      "open_folder",
                                    ])
                              }
                            >
                              <span className="firmware__folderName">
                                {f.displayName}/
                              </span>
                              {typeof f.fileCount === "number" && (
                                <span className="firmware__folderCount">
                                  ({f.fileCount})
                                </span>
                              )}
                            </button>

                            {canManage && (
                              <div className="firmware__folderItemActions">
                                {!isChannel && (
                                  <button
                                    type="button"
                                    className="firmware__iconAction firmware__iconAction--edit"
                                    title={t([
                                      "firmwarePage.renameFolder",
                                      "rename_folder",
                                    ])}
                                    aria-label={t([
                                      "firmwarePage.renameFolder",
                                      "rename_folder",
                                    ])}
                                    onClick={() => openRenameForFolderItem(f)}
                                    disabled={isDeleting}
                                  >
                                    <span aria-hidden="true">✏️</span>
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className={`firmware__iconAction firmware__iconAction--delete ${
                                    isDeleting
                                      ? "firmware__iconAction--loading"
                                      : ""
                                  }`}
                                  title={t([
                                    "firmwarePage.deleteButton",
                                    "btn_delete",
                                  ])}
                                  aria-label={t([
                                    "firmwarePage.deleteButton",
                                    "btn_delete",
                                  ])}
                                  onClick={() => handleDeleteFolder(f)}
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? <FaSpinner /> : <FaTrash />}
                                </button>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {visibleFiles.length > 0 && (
                  <div className="firmware__section">
                    <h3 className="firmware__sectionTitle">
                      {t(["firmwarePage.files", "files"])}
                    </h3>

                    <div className="firmware__tableWrap">
                      <table className="firmware__table">
                        <thead>
                          <tr>
                            <th>{t(["firmwarePage.table.file", "th_file"])}</th>
                            <th className="firmware__colDate">
                              {t([
                                "firmwarePage.table.uploadDate",
                                "th_upload_date",
                              ])}
                            </th>
                            <th>{t(["firmwarePage.table.note", "th_note"])}</th>
                            <th className="firmware__colSize">
                              {t(["firmwarePage.table.size", "th_size"])}
                            </th>
                            <th className="firmware__colAction">
                              {t(["firmwarePage.table.action", "th_action"])}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleFiles.map((file) => {
                            const fileKey = getFileKey(file) || "";
                            const isDownloading = downloadingKey === fileKey;
                            const isDeleting = deletingFileKey === fileKey;

                            const note = file?.notes ? String(file.notes) : "";
                            const noteShort = note ? shortText(note, 60) : "";
                            const displayFileName =
                              file?.originalFilename || prettyFileName(file);

                            return (
                              <tr
                                key={
                                  file.id || file.key || file.path || file.name
                                }
                              >
                                <td>{displayFileName}</td>
                                <td className="firmware__colDate">
                                  {formatCreatedAt(file?.createdAt)}
                                </td>
                                <td title={note || ""}>{noteShort || "—"}</td>
                                <td className="firmware__colSize">
                                  {humanSize(file?.size)}
                                </td>
                                <td className="firmware__colAction">
                                  <div className="firmware__actionsRow">
                                    <button
                                      type="button"
                                      className={`firmware__iconAction firmware__iconAction--download ${
                                        isDownloading
                                          ? "firmware__iconAction--loading"
                                          : ""
                                      }`}
                                      onClick={() => handleDownload(file)}
                                      disabled={isDownloading || isDeleting}
                                      title={t("firmwarePage.downloadFile")}
                                      aria-label={t(
                                        "firmwarePage.downloadFile",
                                      )}
                                    >
                                      {isDownloading ? (
                                        <FaSpinner />
                                      ) : (
                                        <FaDownload />
                                      )}
                                    </button>

                                    {canManage && (
                                      <button
                                        type="button"
                                        className={`firmware__iconAction firmware__iconAction--delete ${
                                          isDeleting
                                            ? "firmware__iconAction--loading"
                                            : ""
                                        }`}
                                        onClick={() => handleDeleteFile(file)}
                                        disabled={isDeleting || isDownloading}
                                        title={t("firmwarePage.deleteFile")}
                                        aria-label={t(
                                          "firmwarePage.deleteFile",
                                        )}
                                      >
                                        {isDeleting ? (
                                          <FaSpinner />
                                        ) : (
                                          <FaTrash />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {visibleFolderItems.length === 0 &&
                  visibleFiles.length === 0 && (
                    <p className="firmware__empty">
                      {t(["firmwarePage.emptyFolder", "empty_folder"])}
                    </p>
                  )}
              </>
            )}

            {uploadOpen && (
              <div className="firmwareModalOverlay" onClick={closeUploadModal}>
                <div
                  className="firmwareModal firmwareModal--upload"
                  onClick={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-modal="true"
                >
                  <div className="firmwareModal__header firmwareModal__header--upload">
                    <h3>{t("firmwarePage.uploadTitle")}</h3>

                    <button
                      type="button"
                      className="firmwareModal__close"
                      onClick={closeUploadModal}
                      disabled={uploading}
                      aria-label={t("firmwarePage.closeUploadModal")}
                    >
                      <FaTimes />
                    </button>
                  </div>

                  <div className="firmwareModal__body">
                    <div className="firmware__target">
                      <b>{t(["firmwarePage.target", "target"])}</b>{" "}
                      {breadcrumbDisplay?.[0]?.displayName ||
                        uploadContext.device}
                      {" / "}
                      {breadcrumbDisplay?.[1]?.displayName ||
                        uploadContext.variant}
                      {uploadContext.isFolderLevel && (
                        <>
                          {" / "}
                          {breadcrumbDisplay?.[2]?.displayName ||
                            uploadContext.folder}
                        </>
                      )}
                    </div>

                    <form
                      className="firmware__formGrid"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleUpload();
                      }}
                    >
                      <label className="firmware__field">
                        <span className="firmware__fieldLabel">
                          {t(["firmwarePage.file", "file"])}
                        </span>

                        <div
                          className={`firmwareDropzone ${
                            dragActive ? "firmwareDropzone--active" : ""
                          } ${uploading ? "firmwareDropzone--disabled" : ""}`}
                          onDragOver={onDropZoneDragOver}
                          onDragEnter={onDropZoneDragOver}
                          onDragLeave={onDropZoneDragLeave}
                          onDrop={onDropZoneDrop}
                        >
                          <input
                            id="firmware-upload-file"
                            className="firmware__fileInput"
                            type="file"
                            onChange={(e) =>
                              handlePickedFile(e.target.files?.[0] || null)
                            }
                            disabled={uploading}
                          />

                          <label
                            htmlFor="firmware-upload-file"
                            className={`firmware__fileButton ${
                              uploading ? "firmware__fileButton--disabled" : ""
                            }`}
                          >
                            {t("firmwarePage.fileChoose")}
                          </label>

                          <div className="firmwareDropzone__text">
                            {t("firmwarePage.dragDropHint")}
                          </div>

                          <span
                            className="firmware__fileName"
                            title={uploadFile?.name || ""}
                          >
                            {uploadFile?.name ||
                              t("firmwarePage.fileNotSelected")}
                          </span>
                        </div>
                      </label>

                      <label className="firmware__field">
                        <span className="firmware__fieldLabel">
                          {t(["firmwarePage.noteOptional", "note_optional"])}
                        </span>
                        <input
                          type="text"
                          placeholder={t([
                            "firmwarePage.notePlaceholder",
                            "firmwarePage.noteOptional",
                            "note_optional",
                          ])}
                          value={uploadNotes}
                          onChange={(e) => setUploadNotes(e.target.value)}
                          disabled={uploading}
                        />
                      </label>

                      <div className="firmwareModal__actions firmwareModal__actions--upload">
                        <button
                          type="button"
                          className="firmware__btn"
                          onClick={closeUploadModal}
                          disabled={uploading}
                        >
                          {t(["firmwarePage.cancel", "cancel"])}
                        </button>

                        <button
                          type="submit"
                          className="firmware__btn firmware__btn--success"
                          disabled={uploading || !uploadFile}
                        >
                          {uploading
                            ? t(["firmwarePage.uploading", "uploading"])
                            : t(["firmwarePage.uploadButton", "btn_upload"])}
                        </button>
                      </div>

                      {(uploading || uploadProgress > 0) && (
                        <div className="firmware__progressWrap">
                          <div className="firmware__progressLabel">
                            {t(["firmwarePage.progress", "progress"])}{" "}
                            <b>{uploadProgress}%</b>
                          </div>
                          <progress
                            className="firmware__progress"
                            value={uploadProgress}
                            max="100"
                          />
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            )}

            {renameOpen && (
              <div className="firmwareModalOverlay" onClick={closeRename}>
                <div
                  className="firmwareModal"
                  onClick={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-modal="true"
                >
                  <div className="firmwareModal__header">
                    <h3>{t(["firmwarePage.renameFolder", "rename_folder"])}</h3>
                  </div>

                  <div className="firmwareModal__body">
                    <div className="firmwareModal__path">
                      {t(["firmwarePage.path", "path"])}{" "}
                      <code>{renameTarget?.path}</code>
                    </div>

                    <form
                      onSubmit={handleRenameSubmit}
                      className="firmwareModal__form"
                    >
                      <label className="firmware__field">
                        <span className="firmware__fieldLabel">
                          {t(["firmwarePage.newName", "new_name"])}
                        </span>
                        <input
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          disabled={renameSaving}
                          autoFocus
                        />
                      </label>

                      {renameError && (
                        <div className="firmwareModal__error">
                          {renameError}
                        </div>
                      )}

                      <div className="firmwareModal__actions">
                        <button
                          type="button"
                          className="firmware__btn"
                          onClick={closeRename}
                          disabled={renameSaving}
                        >
                          {t(["firmwarePage.cancel", "cancel"])}
                        </button>

                        <button
                          type="submit"
                          className="firmware__btn firmware__btn--primary"
                          disabled={renameSaving}
                        >
                          {renameSaving
                            ? t(["firmwarePage.saving", "saving"])
                            : t(["firmwarePage.save", "save"])}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
